// packages/shared-components/src/hooks/useKeyboardLightingSettings.ts

import { useState, useEffect, useCallback, useRef } from "react";
import type {
  KeyboardSDK,
  LightingBaseConfig,
  GetLightingBaseParams,
} from "../types/sdk";
import { isDeviceSessionStandbyRuntime } from "../utils/remoteDeviceConfigEvents";
import {
  useOwnerResyncParticipant,
  fingerprintJson,
  isOwnerResyncLoading,
} from "../utils/deviceSessionOwnerResync";

export interface UseKeyboardLightingSettingsOptions {
  /** SDK API 实例 */
  sdkApi?: KeyboardSDK;
  /** 灯光区域，默认 "Keyboard" */
  area?: string;
  /** 配置类型，默认 "Base" */
  config?: string;
  /** 灯的类型，默认 "DoubleLighting" */
  lamp?: string;
  /** 是否自动加载，默认 true */
  autoLoad?: boolean;
  /** 加载成功回调 */
  onLoadSuccess?: (settings: LightingBaseConfig) => void;
  /** 更新成功回调 */
  onUpdateSuccess?: (settings: LightingBaseConfig) => void;
  /** 错误回调 */
  onError?: (error: Error) => void;
  /** 停止加载 */
  stopLoadSettings?: () => void;
}

export interface UseKeyboardLightingSettingsResult {
  /** 当前灯光设置 */
  settings: LightingBaseConfig | null;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 是否正在更新 */
  isUpdating: boolean;
  /** 当前使用的参数 */
  currentParams: {
    area: string;
    config: string;
    lamp: string;
  };
  /** 刷新设置 */
  refresh: (options?: {
    silent?: boolean;
  }) => Promise<void | LightingBaseConfig | null>;
  /** 更新设置 */
  updateSettings: (
    newSettings: Partial<Omit<LightingBaseConfig, "area">>
  ) => Promise<void>;
  /** 更新单个字段 */
  updateField: <K extends keyof Omit<LightingBaseConfig, "area">>(
    field: K,
    value: Omit<LightingBaseConfig, "area">[K]
  ) => Promise<void>;
  /** 切换参数（可选带 data，带 data 则设置配置，否则加载配置） */
  switchParams: (params: {
    area?: string;
    config?: string;
    lamp?: string;
    data?: Omit<LightingBaseConfig, "area">;
  }) => Promise<void>;
  stopLoadSettings: () => void;
}

export function useKeyboardLightingSettings(
  options: UseKeyboardLightingSettingsOptions = {}
): UseKeyboardLightingSettingsResult {
  const {
    sdkApi,
    area: initialArea = "Keyboard",
    config: initialConfig = "Base",
    lamp: initialLamp = "DoubleLighting",
    autoLoad = true,
    onLoadSuccess,
    onUpdateSuccess,
    onError,
  } = options;
  const { sdkKeyboardBasicApi } = sdkApi || {};
  // 用于判断华奋达
  const getCurrentEquipmentData = sdkKeyboardBasicApi?.getCurrentEquipmentData;
  const solutionRovider = getCurrentEquipmentData?.()?.solutionRovider;
  const [params, setParams] = useState({
    area: initialArea,
    config: initialConfig,
    lamp: initialLamp,
  });

  /** 与 params 同步，供异步回调合并时使用，避免 switchParams 闭包拿到旧的 params */
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const [settings, setSettings] = useState<LightingBaseConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const timerRef = useRef<any>(null);

  // 加载灯光设置
  const loadSettings = useCallback(
    async (options?: {
      silent?: boolean;
    }): Promise<LightingBaseConfig | null | void> => {
      if (!sdkApi?.sdkKeyboardLightingApi) {
        return null;
      }
      if (isDeviceSessionStandbyRuntime()) {
        return null;
      }

      const silent = options?.silent === true;
      if (!silent && !isOwnerResyncLoading()) {
        setIsLoading(true);
      }

      try {
        const p = paramsRef.current;
        const requestParams: GetLightingBaseParams = {
          area: p.area,
          config: p.config,
          lamp: p.lamp,
        };
        const response =
          await sdkApi.sdkKeyboardLightingApi.getLightingBase(requestParams);
        if (response.code === 200 && response.data) {
          if (silent) {
            return response.data;
          }
          setSettings(response.data);
          onLoadSuccess?.(response.data);
          return response.data;
        } else {
          console.warn(
            "[useKeyboardLightingSettings] getLightingBase 暂未就绪:",
            response.message
          );
        }
        return null;
      } catch (error: any) {
        if (!silent) {
          console.error("加载灯光设置失败:", error);
          onError?.(error);
        }
        return null;
      } finally {
        if (!silent && !isOwnerResyncLoading()) {
          setIsLoading(false);
        }
      }
    },
    [sdkApi, onLoadSuccess, onError]
  );

  // 更新灯光设置
  const updateSettings = useCallback(
    async (newSettings: Partial<Omit<LightingBaseConfig, "area">>) => {
      if (!sdkApi?.sdkKeyboardLightingApi || !settings) {
        return;
      }
      if (isDeviceSessionStandbyRuntime()) {
        return;
      }
      if (isOwnerResyncLoading()) {
        return;
      }

      setIsUpdating(true);

      try {
        const updatedData = {
          ...settings,
          ...newSettings,
        };

        const { area: _area, ...dataWithoutArea } = updatedData;
        void _area;
        const returnData = {
          area: params.area,
          config: params.config,
          lamp: params.lamp,
          ...dataWithoutArea,
        };
        const response = await sdkApi.sdkKeyboardLightingApi.setLightingBase({
          area: params.area,
          config: params.config,
          lamp: params.lamp,
          data: dataWithoutArea,
        });

        if (response.code === 200 && response.data) {
          if (
            solutionRovider === "huafenda" ||
            solutionRovider === "rongyuan"
          ) {
            setSettings(returnData);
            onUpdateSuccess?.(returnData);
          } else {
            setSettings(response.data);
            onUpdateSuccess?.(response.data);
          }
        } else {
          throw new Error(response.message || "设置灯光配置失败");
        }
      } catch (error: any) {
        console.error("更新灯光设置失败:", error);
        onError?.(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [sdkApi, settings, params, onUpdateSuccess, onError]
  );

  // 更新单个字段
  const updateField = useCallback(
    async <K extends keyof Omit<LightingBaseConfig, "area">>(
      field: K,
      value: Omit<LightingBaseConfig, "area">[K]
    ) => {
      await updateSettings({ [field]: value } as any);
    },
    [updateSettings]
  );

  // 切换参数（可选带数据）
  const switchParams = useCallback(
    async (newParams: {
      area?: string;
      config?: string;
      lamp?: string;
      data?: Omit<LightingBaseConfig, "area">;
    }) => {
      if (!sdkApi?.sdkKeyboardLightingApi) {
        throw new Error("SDK API 不可用");
      }
      if (isDeviceSessionStandbyRuntime()) {
        return;
      }
      if (isOwnerResyncLoading()) {
        return;
      }

      const { data, ...paramUpdates } = newParams;

      const updatedParams = {
        ...paramsRef.current,
        ...paramUpdates,
      };
      setParams(updatedParams);
      paramsRef.current = updatedParams;

      // 如果带 data，则调用 setLightingBase
      if (data) {
        setIsUpdating(true);
        try {
          const returnData: any = {
            area: updatedParams.area,
            config: updatedParams.config,
            lamp: updatedParams.lamp,
            ...data,
          };
          const baseParams = {
            area: updatedParams.area,
            config: updatedParams.config,
            lamp: updatedParams.lamp,
            data,
          };
          const response =
            await sdkApi.sdkKeyboardLightingApi.setLightingBase(baseParams);
          if (response.code === 200 && response.data) {
            // 华奋达 / 容园：以 UI 入参为准回写 settings，避免 SDK 返回设备原始档位污染界面
            if (
              solutionRovider === "huafenda" ||
              solutionRovider === "rongyuan"
            ) {
              setSettings(returnData);
              onUpdateSuccess?.(returnData);
            } else {
              setSettings(response.data);
              onUpdateSuccess?.(response.data);
            }
          } else {
            throw new Error(response.message || "设置灯光配置失败");
          }
        } catch (error: any) {
          console.error("切换并设置灯光配置失败:", error);
          onError?.(error);
          throw error;
        } finally {
          setIsUpdating(false);
        }
      } else {
        if (isDeviceSessionStandbyRuntime()) {
          return;
        }
        // 否则调用 getLightingBase
        setIsLoading(true);
        try {
          const response = await sdkApi.sdkKeyboardLightingApi.getLightingBase({
            area: updatedParams.area,
            config: updatedParams.config,
            lamp: updatedParams.lamp,
          });
          if (response.code === 200 && response.data) {
            setSettings(response.data);
            onLoadSuccess?.(response.data);
          } else {
            console.warn(
              "[useKeyboardLightingSettings] switchParams getLightingBase 暂未就绪:",
              response.message
            );
          }
        } catch (error: any) {
          console.error("切换并加载灯光配置失败:", error);
          onError?.(error);
          throw error;
        } finally {
          setIsLoading(false);
        }
      }
    },
    [sdkApi, solutionRovider, onLoadSuccess, onUpdateSuccess, onError]
  );

  const stopLoadSettings = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current as any);
      timerRef.current = null;
    }
  }, []);

  // 自动加载
  useEffect(() => {
    // 先清除之前的定时器，防止重复触发
    if (timerRef.current) {
      clearTimeout(timerRef.current as any);
      timerRef.current = null;
    }

    if (autoLoad && (!solutionRovider || solutionRovider !== "beiying")) {
      if (isDeviceSessionStandbyRuntime()) {
        return undefined;
      }
      // 设置新的定时器
      timerRef.current = setTimeout(() => {
        loadSettings();
        // 执行完后清空 ref，可选
        timerRef.current = null;
      }, 300) as any; // as any 是为了兼容浏览器(number)和Node(NodeJS.Timeout)类型差异
    }

    // 组件卸载或依赖变化时清除定时器
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current as any);
        timerRef.current = null;
      }
    };
  }, [autoLoad, loadSettings, solutionRovider]);

  const settingsResyncEnabled = Boolean(
    autoLoad && (!solutionRovider || solutionRovider !== "beiying")
  );
  const probedSettingsRef = useRef<LightingBaseConfig | null>(null);

  useOwnerResyncParticipant(
    {
      id: "keyboard-lighting-settings",
      enabled: () => settingsResyncEnabled,
      probe: async () => {
        const data = (await loadSettings({
          silent: true,
        })) as LightingBaseConfig | null | void;
        if (!data) {
          return { valid: false, fingerprint: "" };
        }
        probedSettingsRef.current = data;
        return { valid: true, fingerprint: fingerprintJson(data) };
      },
      commit: async () => {
        const cached = probedSettingsRef.current;
        probedSettingsRef.current = null;
        if (cached) {
          setSettings(cached);
          onLoadSuccess?.(cached);
        } else {
          await loadSettings();
        }
      },
    },
    settingsResyncEnabled
  );

  return {
    settings,
    isLoading,
    isUpdating,
    currentParams: params,
    refresh: loadSettings,
    updateSettings,
    updateField,
    switchParams,
    stopLoadSettings,
  };
}
